import React, { createContext, useContext, useEffect, useState } from "react";
import { FjordIDProvider, useAuth as useFjordAuth } from "@fjordid/react";
import i18n from "../i18n/index.js";
import type { UserRole } from "@lakseborsen/shared";

// Runtime config injected by Caddy entrypoint.sh into /config.js
declare global {
  interface Window {
    __config__?: {
      FJORDID_URL?: string;
      FJORDID_REALM?: string;
      FJORDID_CLIENT_ID?: string;
    };
  }
}

const cfg = window.__config__ ?? {};
const FJORDID_CLIENT_ID =
  cfg.FJORDID_CLIENT_ID ?? import.meta.env.VITE_FJORDID_CLIENT_ID ?? "";

// ── Offline session cache ─────────────────────────────────────────────────
// FjordAuth's initial "check-sso" needs a network round-trip to the auth
// server (a silent iframe), with a ~10s timeout before it gives up. If the
// device is genuinely offline when the app cold-loads (a dead zone, or the
// PWA restarted mid-trip) that check can never succeed — so a previously
// logged-in fisher would be stuck looking at a "please log in" screen
// instead of the catch form, unable to reach the offline queue at exactly
// the moment it exists for. We can't complete a real login offline, but we
// don't need to: queuing a catch offline doesn't require a valid token
// (see offlineQueue.ts), only submitting it later does. So we remember
// whether this device has ever had a valid session, and let ProtectedRoute
// treat "offline + previously authenticated here" as good enough to show
// the form — reconciling for real once the network (and check-sso) return.
const HAD_SESSION_KEY = "lakseborsen:had_session";

export function hadRecentSession(): boolean {
  try {
    return localStorage.getItem(HAD_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function markSessionSeen() {
  try {
    localStorage.setItem(HAD_SESSION_KEY, "1");
  } catch {
    // ignore — worst case, a future offline cold-load can't skip the check
  }
}

function clearSessionSeen() {
  try {
    localStorage.removeItem(HAD_SESSION_KEY);
  } catch {
    // ignore
  }
}

/** Parse OAuth error params FjordID may append to redirect_uri on failure */
function getOAuthError(): string | null {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  if (!error) return null;
  const desc = params.get("error_description");
  const messages: Record<string, string> = {
    unauthorized_client: `FjordID-klienten "${FJORDID_CLIENT_ID}" er ikke registrert. Be administrator opprette klienten i FjordID.`,
    access_denied: "Tilgang ble nektet av innloggingstjenesten.",
    temporarily_unavailable:
      "Innloggingstjenesten er midlertidig utilgjengelig. Prøv igjen om litt.",
    invalid_client: `Ugyldig FjordID-klient "${FJORDID_CLIENT_ID}". Sjekk at klient-ID er korrekt.`,
  };
  return (
    messages[error] ??
    (desc
      ? decodeURIComponent(desc.replace(/\+/g, " "))
      : `Innloggingsfeil: ${error}`)
  );
}

interface AuthState {
  isAuthenticated: boolean;
  /** True while completing a login redirect callback (shows loading on all pages) */
  isLoading: boolean;
  /** True while background check-sso is running (use in ProtectedRoute to avoid login flash) */
  isChecking: boolean;
  /**
   * True when either genuinely authenticated, or offline with a session
   * previously seen on this device (see hadRecentSession()). Registration
   * UI that should keep working offline (the catch form, the "log a
   * fishing day" prompt) should gate on this instead of isAuthenticated,
   * which stays false offline until check-sso can reach the network.
   */
  hasUsableSession: boolean;
  authError: string | undefined;
  token: string | undefined;
  userSub: string | undefined;
  userName: string | undefined;
  isAdmin: boolean;
  isRiverAdmin: boolean;
  isValdOwner: boolean;
  ownedValdIds: string[];
  /** River system ID this user administers. Only set when isRiverAdmin is true. */
  riverAdminSystemId: string | null;
  /** Returns a fresh access token, refreshing it first if it expires within 30s */
  getToken: () => Promise<string>;
  ensureAuth: () => void;
  login: () => void;
  register: () => void;
  logout: () => void;
  /** Trigger a Keycloak Application-Initiated Action (e.g. passkey registration). */
  loginWithAction: (
    action: string,
    opts?: { redirectUri?: string; locale?: string },
  ) => void;
}

const AuthContext = createContext<AuthState>({
  isAuthenticated: false,
  isLoading: false,
  isChecking: true,
  hasUsableSession: false,
  authError: undefined,
  token: undefined,
  userSub: undefined,
  userName: undefined,
  isAdmin: false,
  isRiverAdmin: false,
  isValdOwner: false,
  ownedValdIds: [],
  riverAdminSystemId: null,
  getToken: () => Promise.resolve(""),
  ensureAuth: () => {},
  login: () => {},
  register: () => {},
  logout: () => {},
  loginWithAction: () => {},
});

/** Inner component — consumes @fjordid/react state and layers app-specific role logic on top. */
function AuthInner({
  authError: oauthError,
  children,
}: {
  authError: string | undefined;
  children: React.ReactNode;
}) {
  const {
    auth,
    authenticated,
    isInitializing,
    isRedirectCallback,
    user,
    login,
    register,
    logout,
    getToken,
  } = useFjordAuth();

  const [role, setRole] = useState<UserRole>("fisher");
  const [ownedValdIds, setOwnedValdIds] = useState<string[]>([]);
  const [riverAdminSystemId, setRiverAdminSystemId] = useState<string | null>(
    null,
  );
  const [authError, setAuthError] = useState<string | undefined>(oauthError);
  const [isOffline, setIsOffline] = useState(() => !navigator.onLine);

  // Remember (locally, per-device) that this device has had a valid
  // session, so a later fully-offline cold-load can skip the blocking
  // check-sso round trip instead of showing a login screen — see
  // hadRecentSession() above.
  useEffect(() => {
    if (authenticated) markSessionSeen();
  }, [authenticated]);

  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Fetch app-level role whenever auth state changes
  useEffect(() => {
    if (!authenticated) {
      setRole("fisher");
      setOwnedValdIds([]);
      setRiverAdminSystemId(null);
      return;
    }
    getToken()
      .then((token) => {
        if (!token) return;
        return fetch("/api/v1/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
      })
      .then((res) => {
        if (!res?.ok) return;
        return res.json();
      })
      .then((profile) => {
        if (!profile) return;
        if (profile.role) setRole(profile.role as UserRole);
        if (Array.isArray(profile.ownedValdIds))
          setOwnedValdIds(profile.ownedValdIds as string[]);
        setRiverAdminSystemId(profile.riverAdminSystemId ?? null);
      })
      .catch(() => {
        // Non-critical — default to "fisher"
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  // Surface OIDC callback exchange failures as user-visible errors
  useEffect(() => {
    const wasCallback = new URLSearchParams(window.location.search).has("code");
    return auth.on("onAuthError", () => {
      if (wasCallback) {
        setAuthError(
          "Kunne ikke koble til innloggingstjenesten. Prøv igjen om litt.",
        );
      }
    });
  }, [auth]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: authenticated,
        isLoading: isRedirectCallback,
        isChecking: isInitializing,
        hasUsableSession: authenticated || (isOffline && hadRecentSession()),
        authError,
        token: auth.token,
        userSub: user?.id,
        userName: user?.name,
        isAdmin: role === "admin",
        isRiverAdmin: role === "river_admin",
        isValdOwner: ownedValdIds.length > 0,
        ownedValdIds,
        riverAdminSystemId,
        getToken: async () => (await getToken()) ?? "",
        ensureAuth: () => {},
        login: () =>
          login({
            redirectUri: window.location.origin + window.location.pathname,
            locale: i18n.language,
          }),
        register: () =>
          register({
            redirectUri: window.location.origin + window.location.pathname,
          }),
        logout: () => {
          clearSessionSeen();
          logout(window.location.origin + "/");
        },
        loginWithAction: (action, opts) =>
          login({
            action,
            redirectUri:
              opts?.redirectUri ??
              window.location.origin + window.location.pathname,
            locale: opts?.locale ?? i18n.language,
          }),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const oauthError = getOAuthError();
  const [authError] = useState<string | undefined>(oauthError ?? undefined);

  // Clean OAuth error params from URL once on mount
  useEffect(() => {
    if (!oauthError) return;
    const clean = new URL(window.location.href);
    clean.searchParams.delete("error");
    clean.searchParams.delete("error_description");
    clean.searchParams.delete("state");
    window.history.replaceState({}, "", clean.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FjordIDProvider
      options={{
        authUrl:
          cfg.FJORDID_URL ??
          import.meta.env.VITE_FJORDID_URL ??
          "https://auth.fjordid.eu",
        realm:
          cfg.FJORDID_REALM ?? import.meta.env.VITE_FJORDID_REALM ?? "fjordid",
        clientId: FJORDID_CLIENT_ID,
      }}
    >
      <AuthInner authError={authError}>{children}</AuthInner>
    </FjordIDProvider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
